import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { SafeResourceUrl } from '@angular/platform-browser';
import {
  File as IonicNativeFile,
  FileEntry,
  IFile,
} from '@awesome-cordova-plugins/file/ngx';
import { Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import urljoin from 'url-join';

import { APIService } from 'src/app/services/api.service';
import { UserService } from 'src/app/services/user.service';
import { TrainingVideo } from 'src/app/models/skill';
import { PlayerTraining, Task } from 'src/app/models/player-training';
import { PlayerRound } from 'src/app/models/round-history';
import { environment } from 'src/environments/environment';
import { BaseWebSite } from '../shared/data/constants';

@Injectable({
  providedIn: 'root',
})
export class PlayerService extends APIService {
  private uploadsInProgress: Set<SafeResourceUrl> = new Set();
  readonly onUpdate$ = new Subject<void>();

  constructor(
    private userService: UserService,
    private http: HttpClient,
    private file: IonicNativeFile
  ) {
    super();
  }

  public getProfileLink(identifier: string) {
    return urljoin(BaseWebSite, 'user', identifier);
  }

  public async getUkixxLeagues() {
    const url = urljoin(this.BASE_URL, `skill-trainings/ukixx-leagues/`);
    return this.http.get(url).toPromise();
  }

  public async getPlayerRound(roundId: number | string = 'current') {
    const userId = await this.userService.userId;
    const url = urljoin(
      this.BASE_URL,
      `skill-trainings/players/${userId}/rounds/${roundId}/`
    );
    return this.http.get<PlayerRound>(url).toPromise();
  }

  public async getAllPlayerRounds() {
    const userId = await this.userService.userId;
    const url = urljoin(
      this.BASE_URL,
      `skill-trainings/players/${userId}/rounds/`
    );
    return this.http.get<PlayerRound[]>(url).toPromise();
  }

  public async getPlayerTasksHistory() {
    const userId = await this.userService.userId;
    const url = urljoin(this.BASE_URL, `tasks/players/${userId}/`);
    return this.http.get<Task[]>(url).toPromise();
  }

  public async getAssignedTraining(id: number) {
    const url = urljoin(
      this.BASE_URL,
      `skill-trainings/players/trainings/${id}/`
    );
    return this.http.get<PlayerTraining>(url).toPromise();
  }

  public async validatePlayerPosition(body) {
    const url = urljoin(this.BASE_URL, `users/positions/validate/`);
    return this.http.post<PlayerTraining>(url, body).toPromise();
  }

  async recursiveCreateDir(pathParts: string[]) {
    let currentRelativePath = this.file.dataDirectory;
    for (const dirName of pathParts) {
      try {
        await this.file.checkDir(currentRelativePath, dirName);
      } catch (err) {
        console.warn(err);
        await this.file.createDir(currentRelativePath, dirName, false);
      }
      currentRelativePath = urljoin(currentRelativePath, dirName);
      if (currentRelativePath.substr(-1) !== '/') {
        currentRelativePath += '/';
      }
    }
  }

  private convertFileEntryToCordovaFile(fileEntry: FileEntry): Promise<IFile> {
    return new Promise<IFile>((resolve, reject) => {
      fileEntry.file(resolve, reject);
    });
  }

  private convertCordovaFileToJavascriptFile(
    cordovaFile: IFile
  ): Promise<File> {
    return new Promise<File>((resolve, reject) => {
      const reader = this.getFileReader();
      reader.onloadend = () => {
        if (reader.error) {
          reject(reader.error);
        } else {
          const blob: any = new Blob([reader.result], {
            type: cordovaFile.type,
          });
          blob.lastModifiedDate = new Date();
          blob.name = cordovaFile.name;
          resolve(blob as File);
        }
      };
      reader.readAsArrayBuffer(cordovaFile);
    });
  }

  private getFileReader(): FileReader {
    const fileReader = new FileReader();
    const zoneOriginalInstance = (fileReader as any)
      .__zone_symbol__originalInstance;
    return zoneOriginalInstance || fileReader;
  }

  public markVideoAsUploading(video: TrainingVideo) {
    return this.uploadsInProgress.add(video.secureURL);
  }

  private markVideoAsComplete(video: TrainingVideo) {
    return this.uploadsInProgress.delete(video.secureURL);
  }

  public isVideoUploading(video: TrainingVideo): boolean {
    return this.uploadsInProgress.has(video.secureURL);
  }

  public async uploadTrainingVideo(video: TrainingVideo, trainingId: number) {
    // Fetch file stats and file contents from filesystem
    const cdvFile = await this.convertFileEntryToCordovaFile(
      video.fileEntry as FileEntry
    );
    const jsFile = await this.convertCordovaFileToJavascriptFile(cdvFile);

    // Upload the actual file to the Storage server
    const videoUploadUrl = environment.production
      ? video.file
      : video.file.replace(/localhost/, window.location.hostname);
    await this.http.put(videoUploadUrl, jsFile).toPromise();

    // Mark file as uploaded on API server
    const videoCompleteUrl = urljoin(
      this.BASE_URL,
      `skill-trainings/players/trainings/${trainingId}/video/complete/`
    );
    return this.http
      .post<TrainingVideo>(videoCompleteUrl, null)
      .pipe(
        tap(() => {
          this.onUpdate$.next();
          this.markVideoAsComplete(video);
        })
      )
      .toPromise();
  }

  public async createNewPresignedUpload(
    video: TrainingVideo,
    trainingId: number
  ) {
    // Fetch file stats and file contents from filesystem
    const cdvFile = await this.convertFileEntryToCordovaFile(
      video.fileEntry as FileEntry
    );
    const jsFile = await this.convertCordovaFileToJavascriptFile(cdvFile);

    // Create presigned upload URL
    const videoCreateUrl = urljoin(
      this.BASE_URL,
      `skill-trainings/players/trainings/${trainingId}/video/`
    );
    const resp = await this.http
      .post<TrainingVideo>(videoCreateUrl, {
        filename: jsFile.name,
        dimension: video.dimension,
      })
      .toPromise();

    this.onUpdate$.next();

    video.file = resp.file;
    // Upload file to storage server directly using presigned URL
    return this.uploadTrainingVideo(video, trainingId);
  }
}
